import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useLanguage } from "contexts/language.context";
import NcrPhotoGallery from "./NcrPhotoGallery";
import { hasNcrTextErrors, ncrTextError } from "../ncrTextValidation";

// The shared picker edits report membership without changing inclusion for creation.
function NcrReportGroups({ groups, visibleRows, loading, toggleRow, groupRows }) {
  const { label } = useLanguage();
  const [action, setAction] = React.useState(null);
  const visibleIds = React.useMemo(
    () => new Set(visibleRows.map((row) => row.auditKpiId)),
    [visibleRows],
  );
  const pickerRows = action
    ? Array.from(groups.values())
        .flat()
        .filter(
          (row) =>
            !row.issued &&
            (visibleIds.has(row.auditKpiId) ||
              action.originalIds.includes(row.auditKpiId)),
        )
    : [];
  const matches = pickerRows.filter((row) =>
    `${row.kpiReferenceCode} ${row.nonConformityDescription}`
      .toLocaleLowerCase()
      .includes(action?.search.toLocaleLowerCase() || ""),
  );
  // One batch request issues every selected report or none, so while the
  // dialog is open it only ever holds draft groups.
  const draftReports = Array.from(groups.entries()).filter(
    ([, group]) =>
      group.some((row) => !row.issued) &&
      group.some((row) => visibleIds.has(row.auditKpiId)),
  );
  return (
    <>
      {draftReports.map(([groupId, group]) => (
        <Box
          component="section"
          aria-labelledby={`draft-report-${groupId}`}
          key={groupId}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: { xs: 1, md: 2 },
                  mb: 2,
                  minWidth: 0,
                  contentVisibility: "auto",
                  containIntrinsicSize: "auto 300px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    <Typography
                      id={`draft-report-${groupId}`}
                      component="h4"
                      variant="h6"
                    >
                      {label("ncr-draft-report") || "Draft report"} {groupId}
                    </Typography>
                    <Typography variant="body2">
                      {group.filter((row) => row.selected).length}{" "}
                      / {group.length}{" "}
                      {label("ncr-kpis-included") || "KPIs included"} ·{" "}
                      {group.map((row) => row.kpiReferenceCode).join(", ")}
                    </Typography>
                  </Box>
                </Box>
                {group
                  .filter((row) => visibleIds.has(row.auditKpiId))
                  .map((row) => (
                    <Box
                      key={row.auditKpiId}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr)",
                          md: "minmax(0, 1fr) 168px",
                        },
                        alignItems: "start",
                        gap: 2,
                        py: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <FormControlLabel
                            sx={{
                              m: 0,
                              display: "flex",
                              alignItems: "flex-start",
                              "& .MuiFormControlLabel-label": {
                                minWidth: 0,
                              },
                            }}
                            label={
                              <Typography fontWeight="bold" variant="body2">
                                {row.kpiReferenceCode}
                              </Typography>
                            }
                            control={
                              <Checkbox
                                checked={row.selected}
                                disabled={loading}
                                onChange={() => toggleRow(row.auditKpiId)}
                                sx={{ pt: 0, pl: 0 }}
                              />
                            }
                          />
                        </Box>
                        <Typography variant="body2">
                          {row.nonConformityDescription}
                        </Typography>
                        {row.correctiveAction ? (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>
                              {label("corrective-action-singular") ||
                                "Corrective action"}
                              :{" "}
                            </strong>
                            {row.correctiveAction}
                          </Typography>
                        ) : null}
                        {hasNcrTextErrors(row) ? (
                          <Typography
                            color="error"
                            variant="body2"
                            role="alert"
                          >
                            {Object.entries({
                              kpiReferenceCode: "Reference code",
                              nonConformityDescription:
                                "Non-conformity description",
                              rootCause: "Root cause",
                              correctiveAction: "Corrective action",
                              remarks: "Remarks",
                            })
                              .map(([field, name]) => {
                                const message = ncrTextError(field, row[field]);
                                return message ? `${name}: ${message} ` : "";
                              })
                              .join("")}
                            {label("ncr-correct-source-text") ||
                              "Correct the source audit text before generating this NCR."}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 1,
                        }}
                      >
                        <Button
                          type="button"
                          disabled={loading}
                          aria-label={`${label("ncr-group-kpis") || "Group KPIs"} · ${row.kpiReferenceCode}`}
                          onClick={() =>
                            setAction({
                              groupId,
                              ids: group.map((member) => member.auditKpiId),
                              originalIds: group.map(
                                (member) => member.auditKpiId,
                              ),
                              search: "",
                            })
                          }
                        >
                          {label("ncr-group-kpis") || "Group KPIs"}
                        </Button>
                        {row.attachments?.length ? (
                          <NcrPhotoGallery
                            photos={row.attachments}
                            reference={row.kpiReferenceCode}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  ))}
        </Box>
      ))}
      {action ? (
        <Dialog
          open
          fullWidth
          maxWidth="md"
          onClose={loading ? undefined : () => setAction(null)}
          aria-labelledby="ncr-group-title"
        >
          <DialogTitle id="ncr-group-title">
            {label("ncr-group-kpis") || "Group KPIs"}
          </DialogTitle>
          <DialogContent dividers>
            <TextField
              fullWidth
              autoFocus
              size="small"
              label={label("ncr-search-kpis") || "Search KPIs"}
              value={action.search}
              onChange={(event) =>
                setAction((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />
            <FormGroup
              sx={{
                mt: 1,
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                columnGap: 3,
                rowGap: 0.5,
                alignContent: "start",
                maxHeight: "60vh",
                overflowY: "auto",
                overflowX: "hidden",
                pr: 1,
              }}
            >
              {matches.map((row) => (
                <FormControlLabel
                  key={row.auditKpiId}
                  sx={{
                    m: 0,
                    py: 0.5,
                    display: "flex",
                    alignItems: "flex-start",
                    width: "100%",
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    "& .MuiFormControlLabel-label": {
                      flex: 1,
                      minWidth: 0,
                    },
                  }}
                  control={
                    <Checkbox
                      disabled={loading}
                      checked={action.ids.includes(row.auditKpiId)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setAction((current) => ({
                          ...current,
                          ids: checked
                            ? [...current.ids, row.auditKpiId]
                            : current.ids.filter((id) => id !== row.auditKpiId),
                        }));
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {row.kpiReferenceCode}
                      </Typography>
                      <Typography variant="body2">
                        {row.nonConformityDescription}
                      </Typography>
                      {!row.selected ? (
                        <Typography variant="caption">
                          {label("ncr-not-selected") ||
                            "Not selected for creation"}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                />
              ))}
              {!matches.length ? (
                <Typography variant="body2">
                  {label("none-found") || "None found"}
                </Typography>
              ) : null}
            </FormGroup>
          </DialogContent>
          <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              type="button"
              disabled={loading}
              onClick={() => setAction(null)}
            >
              {label("cancel-button") || "Cancel"}
            </Button>
            <Button
              type="button"
              disabled={loading || !action.ids.length}
              onClick={() => {
                groupRows(action.ids, action.groupId);
                setAction(null);
              }}
            >
              {label("ncr-group-selected-kpis") ||
                "Group selected KPIs into one NCR"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </>
  );
}
export default React.memo(NcrReportGroups);
